/**
 * TeacherLayout - Main Layout Wrapper for Teacher Pages
 * Material Design 3, mobile-first, app-like design
 * Optimized for low-tech users
 * 
 * Features:
 * - Material App Bar at top
 * - Material Bottom Navigation at bottom
 * - Material Drawer overlay
 * - Proper padding for fixed elements
 * - Safe area handling for mobile
 * - Responsive breakpoints
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MaterialAppBar from './MaterialAppBar';
import MaterialBottomNav from './MaterialBottomNav';
import MaterialDrawer from './MaterialDrawer';

import { useAuthContext } from '../../../contexts/AuthContext';
import MeditationBell from '../../shared/MeditationBell';
import { preloadGathas } from '../../../config/gathas';
import { teacherScheduleApi } from '../../../services/teacherScheduleApi';

// Pre-warm gathas cache on layout mount
preloadGathas();

/**
 * TeacherLayout Component
 * Wraps all teacher pages with Material Design layout
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title for app bar
 * @param {React.ReactNode} props.appBarActions - Additional actions for app bar (e.g., DivisionSwitcher)
 * @param {React.ReactNode} props.contextSwitcher - Custom context switcher component
 * @param {boolean} props.hideContext - Hide default context display
 * @param {boolean} props.showBack - Show back button instead of menu
 * @param {Function} props.onBackClick - Callback for back button
 */
export default function TeacherLayout({
  children,
  title = 'My Classes',
  appBarActions,
  contextSwitcher,
  hideContext = false, // New prop
  showBack = false,    // New prop
  onBackClick,         // New prop
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const pollRef = useRef(null);

  // Fetch unread notification count
  const fetchCount = useCallback(async () => {
    try {
      const response = await teacherScheduleApi.getNotificationCount();
      setUnreadCount(response.count || 0);
    } catch (e) {
      // Silent fail — don't disrupt layout for notification count
    }
  }, []);

  // Poll every 60s + refresh on window focus
  useEffect(() => {
    fetchCount();
    pollRef.current = setInterval(fetchCount, 60000);

    const handleFocus = () => fetchCount();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchCount]);

  // Suppress badge if notification mode is "off"
  const notificationMode = typeof window !== 'undefined'
    ? localStorage.getItem('notification_mode') || 'all'
    : 'all';
  const displayCount = notificationMode === 'off' ? 0 : unreadCount;

  // Handle drawer toggle
  const handleMenuClick = () => {
    setDrawerOpen(true);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // Handle profile click (opens drawer via bottom nav)
  const handleProfileClick = () => {
    setDrawerOpen(true);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
      }
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-background-warm">
      {/* Material App Bar - Fixed at top */}
      <MaterialAppBar
        title={title}
        onMenuClick={handleMenuClick}
        showNotifications={true}
        unreadCount={displayCount}
        onRefreshCount={fetchCount}
        contextSwitcher={contextSwitcher}
        hideContext={hideContext}
        showBack={showBack}
        onBackClick={onBackClick}
      >
        {appBarActions}
      </MaterialAppBar>

      {/* Main Content Area - Padding for app bar (56px) and bottom nav (64px) */}
      <main
        className="pt-14 pb-20 min-h-screen"
        style={{
          paddingTop: '56px', // Space for app bar (h-14)
          paddingBottom: '80px', // Space for bottom nav + extra for safe area
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          {children}
        </div>
      </main>

      {/* Material Bottom Navigation - Fixed at bottom */}
      <MaterialBottomNav onProfileClick={handleProfileClick} unreadCount={displayCount} />

      {/* Material Drawer - Overlay */}
      <MaterialDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        onLogout={handleLogout}
      />

      <MeditationBell bottomOffset={80} />

      {/* Safe area handling for mobile devices with notches */}
      <style>{`
        @supports (padding: max(0px)) {
          .min-h-screen {
            padding-top: max(env(safe-area-inset-top), 0px);
            padding-bottom: max(env(safe-area-inset-bottom), 0px);
          }
        }
      `}</style>
    </div>
  );
}

